import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Dialog,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  Grid,
  Box,
  FormControlLabel,
  InputLabel,
  FormControl,
  FormGroup,
  FormHelperText,
  Paper,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import React, { useState, useEffect } from "react";
import { useIncidents } from "./IncidentEntryContext";
import {
  updateIncident,
  createIncident,
  resolveLocation,
  closeIncident,
} from "./utils";

const isNoShoulderImpact = (value, shoulderImpacts) => {
  if (!value) return true;
  const noneOption = shoulderImpacts?.find((opt) =>
    /^none$/i.test(opt.label ?? ""),
  );
  return noneOption ? value === noneOption.value : false;
};

const EditIncidentForm = () => {
  const [open, setOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [editedIncident, setEditedIncident] = useState(null);
  const [ambiguousCandidates, setAmbiguousCandidates] = useState(null);

  const {
    incident,
    newIncident,
    selectedIncident,
    setSelectedIncident,
    setNewIncident,
    setReload,
    dropdowns,
  } = useIncidents();

  useEffect(() => {
    setOpen(
      (selectedIncident !== null && incident !== null) || newIncident !== null,
    );
    if (
      newIncident &&
      (!editedIncident || editedIncident.id !== newIncident.id)
    ) {
      const initialized = {
        ...newIncident,
        // Only default sided shoulders when unspecifiedShoulder is absent
        ...(newIncident.unspecifiedShoulder
          ? {}
          : {
              leftShoulder: newIncident.leftShoulder || "Open",
              rightShoulder: newIncident.rightShoulder || "Open",
            }),
      };
      setEditedIncident(initialized);
      validateForm(initialized);
    } else if (
      incident &&
      selectedIncident &&
      (!editedIncident || editedIncident.id !== incident.id)
    ) {
      const normalizeShoulders = (obj) => {
        if (!obj.unspecifiedShoulder) return obj;
        return { ...obj, leftShoulder: "", rightShoulder: "" };
      };
      const normalized = {
        ...normalizeShoulders(incident),
        locations: (incident.locations || []).map(normalizeShoulders),
      };
      setEditedIncident(normalized);
    } else if (!selectedIncident && !newIncident) {
      setEditedIncident(null);
      setFormErrors({});
      setAmbiguousCandidates(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIncident, incident, newIncident]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    if (field === "locationDescription") {
      // Editing the text invalidates any prior resolution result
      setAmbiguousCandidates(null);
      setEditedIncident((prev) => ({
        ...prev,
        locationDescription: value,
        resolvedLocation: null,
        locationResolved: false,
        locationResolutionStatus: "",
      }));
      return;
    }
    setEditedIncident((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCheckboxChange = (field) => (e) => {
    const checked = e.target.checked;
    setEditedIncident((prev) => ({
      ...prev,
      [field]: checked,
    }));
  };

  const handleDateTimeChange = (field) => (newValue) => {
    setEditedIncident((prev) => ({
      ...prev,
      [field]: newValue,
    }));
  };

  const handleLaneCountChange = (field) => (e) => {
    const inputValue = e.target.value;
    const value = inputValue === "" ? "" : parseInt(inputValue) || 0;
    setEditedIncident((prev) => {
      const newLocations = prev.locations
        ? prev.locations.map((loc) => ({ ...loc, lanes: [] }))
        : prev.locations;
      const laneImpacts =
        value > 0
          ? Array.from({ length: value }, (_, i) => ({
              laneNumber: i + 1,
              impact: "Open",
            }))
          : [];

      return {
        ...prev,
        [field]: value,
        laneImpacts,
        locations: newLocations,
      };
    });
  };

  const handleResolveLocation = async () => {
    const isEditMode =
      editedIncident.id !== null && editedIncident.id !== undefined;
    const locationDesc = isEditMode
      ? editedIncident.locations?.[0]?.description || ""
      : editedIncident.locationDescription || "";

    if (!locationDesc || locationDesc.trim() === "") {
      alert("Please enter a location description");
      return;
    }

    try {
      const locationData = {
        locationDescription: locationDesc,
        userLaneCount: editedIncident.userLaneCount || 0,
      };

      const response = await resolveLocation(editedIncident.id, locationData);

      if (response.success && response.data) {
        if (
          response.data.ambiguous === true &&
          response.data.locations &&
          response.data.locations.length > 0
        ) {
          // Multiple candidates — let the operator pick one
          setAmbiguousCandidates(response.data.locations);
          // Drop the resolution carried on the loaded locations too. updateIncident
          // falls back to locations[0].resolvedLocation when the top-level field is
          // null, so leaving it in place would silently re-send the OLD location
          // alongside the operator's NEW description (#50).
          const updatedIncident = {
            ...editedIncident,
            locations: (editedIncident.locations || []).map((loc) => ({
              ...loc,
              resolvedLocation: null,
            })),
            resolvedLocation: null,
            locationResolved: false,
            locationResolutionStatus: "AMBIGUOUS",
          };
          setEditedIncident(updatedIncident);
          validateForm(updatedIncident);
          return;
        }

        if (response.data.resolved === false) {
          setAmbiguousCandidates(null);
          const updatedIncident = {
            ...editedIncident,
            locations: (editedIncident.locations || []).map((loc) => ({
              ...loc,
              resolvedLocation: null,
            })),
            resolvedLocation: null,
            locationResolved: false,
            locationResolutionStatus: "FAILED",
          };
          setEditedIncident(updatedIncident);
          validateForm(updatedIncident);
          alert(
            (response.data.errorMessage || "Location resolution failed.") +
              (isEditMode
                ? '\n\nTo save the incident anyway, check "Update with unresolved location" and specify User Lane Count.'
                : '\n\nTo create the incident anyway, check "Create with unresolved location" and specify User Lane Count.'),
          );
          return;
        }

        const resolvedLoc =
          response.data.locations && response.data.locations.length > 0
            ? response.data.locations[0]
            : null;
        // Adopt the resolver's description as the incident's location text —
        // fuzzy matches (e.g. "Horlem" -> Harlem Ave) would otherwise save
        // the operator's typo as the displayed location
        const resolvedText =
          (resolvedLoc && cleanCandidateDescription(resolvedLoc)) ||
          locationDesc;
        const resolvedLocation =
          resolvedLoc && resolvedLoc.resolvedLocation
            ? { ...resolvedLoc.resolvedLocation, textProfile: resolvedText }
            : null;
        setAmbiguousCandidates(null);
        const resolvedLaneCount =
          response.data.laneCount || editedIncident.maxLaneCount || 0;
        const resolvedLaneImpacts =
          response.data.laneImpacts || editedIncident.laneImpacts || [];
        const filledLaneImpacts =
          resolvedLaneCount > 0
            ? Array.from({ length: resolvedLaneCount }, (_, i) => {
                const n = i + 1;
                return (
                  resolvedLaneImpacts.find((li) => li.laneNumber === n) || {
                    laneNumber: n,
                    impact: "Open",
                  }
                );
              })
            : resolvedLaneImpacts;
        const updatedIncident = {
          ...editedIncident,
          locationDescription: resolvedText,
          maxLaneCount: resolvedLaneCount,
          laneImpacts: filledLaneImpacts,
          locations: response.data.locations || editedIncident.locations,
          resolvedLocation: resolvedLocation,
          createUnresolved: false,
          locationResolved: true,
          locationResolutionStatus: "RESOLVED",
          ...(response.data.locationFields ? response.data.locationFields : {}),
        };

        setEditedIncident(updatedIncident);
        validateForm(updatedIncident);
        // No alert on success — the "✓ Location resolved" line under the field
        // already shows the result
      } else {
        setAmbiguousCandidates(null);
        const updatedIncident = {
          ...editedIncident,
          resolvedLocation: null,
          locationResolved: false,
          locationResolutionStatus: "UNRESOLVED",
        };
        setEditedIncident(updatedIncident);
        validateForm(updatedIncident);
        alert("Location resolution completed, but no lane data returned.");
      }
    } catch (error) {
      alert("Error resolving location: " + error.message);
    }
  };

  // LinearLocation.toString() appends the original input in quotes
  // (e.g. 'WB US-6 and Brandon Rd, Rockdale, Illinois "US-6 at Brandon Rd"');
  // strip that suffix to get the clean directional description.
  const cleanCandidateDescription = (candidate) =>
    (candidate.description || "").replace(/\s*"[^"]*"\s*$/, "");

  const handlePickCandidate = (candidate) => {
    // Adopt the picked candidate's directional description as the incident's
    // location text — otherwise the saved incident (and the map popup) keeps
    // showing the direction-less text the operator originally typed.
    const pickedText =
      cleanCandidateDescription(candidate) ||
      editedIncident.locationDescription;
    const pickedLocation = candidate.resolvedLocation
      ? { ...candidate.resolvedLocation, textProfile: pickedText }
      : null;
    const pickedLaneCount =
      candidate.laneCount || editedIncident.maxLaneCount || 0;
    const pickedLaneImpacts =
      pickedLaneCount > 0
        ? Array.from({ length: pickedLaneCount }, (_, i) => {
            const n = i + 1;
            return (
              editedIncident.laneImpacts?.find((li) => li.laneNumber === n) || {
                laneNumber: n,
                impact: "Open",
              }
            );
          })
        : editedIncident.laneImpacts || [];
    const updatedIncident = {
      ...editedIncident,
      locationDescription: pickedText,
      resolvedLocation: pickedLocation,
      locations: [candidate],
      maxLaneCount: pickedLaneCount,
      laneImpacts: pickedLaneImpacts,
      createUnresolved: false,
      locationResolved: true,
      locationResolutionStatus: "RESOLVED",
    };
    setAmbiguousCandidates(null);
    setEditedIncident(updatedIncident);
    validateForm(updatedIncident);
  };

  const validateForm = (incident) => {
    let errors = {};
    if (!incident.sourceName || incident.sourceName.trim() === "") {
      errors.sourceName = "Source Name is required";
    }

    if (!incident.confidenceLevel || incident.confidenceLevel.trim() === "") {
      errors.confidenceLevel = "Confidence Level is required";
    }

    const hasAnyTime =
      incident.occurrenceTime ||
      incident.detectionTime ||
      incident.verificationTime;
    if (!hasAnyTime) {
      const msg = "At least one time field is required";
      errors.occurrenceTime = msg;
      errors.detectionTime = msg;
      errors.verificationTime = msg;
    }

    const countFields = [
      "automobileCount",
      "busCount",
      "motorcycleCount",
      "pickupTruckCount",
      "semiTrailerCount",
      "tankerTruckCount",
      "fatalityCount",
      "injuryCount",
    ];

    countFields.forEach((field) => {
      const value = incident[field];
      if (value !== undefined && value !== null && (value < 0 || value > 8)) {
        errors[field] = `${field} must be between 0 and 8`;
      }
    });

    const isCreateMode = incident.id === null || incident.id === undefined;
    if (isCreateMode) {
      if (
        !incident.locationDescription ||
        incident.locationDescription.trim() === ""
      ) {
        errors.locationDescription = "Location description is required";
      }

      const isLocationResolved =
        !!incident.resolvedLocation ||
        incident.locationResolved === true ||
        incident.locationResolutionStatus === "RESOLVED";
      const hasUserLaneCount =
        incident.userLaneCount && incident.userLaneCount > 0;
      const wantsUnresolved = incident.createUnresolved === true;

      if (!isLocationResolved && !(wantsUnresolved && hasUserLaneCount)) {
        errors.location_resolution = wantsUnresolved
          ? "Specify User Lane Count — the number of lanes at the unresolved location"
          : 'Resolve the location, or check "Create with unresolved location" and specify User Lane Count';
      }

      if (
        wantsUnresolved &&
        hasUserLaneCount &&
        incident.laneImpacts &&
        incident.laneImpacts.length > 0 &&
        incident.laneImpacts.length !== incident.userLaneCount
      ) {
        errors.laneImpacts = `Set an impact for all ${incident.userLaneCount} lanes, or leave them all unset`;
      }
    } else if (
      incident.locationResolutionStatus === "AMBIGUOUS" ||
      incident.locationResolutionStatus === "FAILED"
    ) {
      // Edit mode. AMBIGUOUS/FAILED are set only by handleResolveLocation in this
      // session (the server sends LOCATION_* names), so this fires only after the
      // operator pressed Resolve Location and the resolution did not complete.
      // Without it the save proceeds and the incident keeps its previous location
      // while displaying the newly typed description (#50).
      if (incident.locationResolutionStatus === "AMBIGUOUS") {
        errors.location_resolution =
          "Select one of the candidate locations listed under Location Description";
      } else {
        const hasUserLaneCount =
          incident.userLaneCount && incident.userLaneCount > 0;
        const wantsUnresolved = incident.createUnresolved === true;
        if (!(wantsUnresolved && hasUserLaneCount)) {
          errors.location_resolution = wantsUnresolved
            ? "Specify User Lane Count — the number of lanes at the unresolved location"
            : 'Resolve the location, or check "Update with unresolved location" and specify User Lane Count';
        }
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm(editedIncident)) {
      const errorMessages = Object.entries(formErrors)
        .map(([field, message]) => `• ${field}: ${message}`)
        .join("\n");
      alert("Please fix the following validation errors:\n\n" + errorMessages);
      return;
    }

    try {
      let result;
      if (editedIncident.id === null || editedIncident.id === undefined) {
        result = await createIncident(editedIncident);
      } else {
        result = await updateIncident(editedIncident.id, editedIncident);
      }

      if (result.success) {
        setSelectedIncident(null);
        setNewIncident(null);
        setEditedIncident(null);
        setFormErrors({});
        setAmbiguousCandidates(null);
        setReload(true);
      }
    } catch (error) {
      alert("Error saving incident: " + error.message);
    }
  };

  const handleClose = async () => {
    if (!editedIncident.id) {
      return;
    }

    // eslint-disable-next-line no-restricted-globals
    if (confirm("Are you sure you want to close this incident?")) {
      try {
        await closeIncident(editedIncident.id);
        setSelectedIncident(null);
        setNewIncident(null);
        setEditedIncident(null);
        setFormErrors({});
        setReload(true);
      } catch (error) {
        alert("Error closing incident: " + error.message);
      }
    }
  };

  const onClose = (e, reason) => {
    if (reason !== undefined) {
      return;
    }
    setSelectedIncident(null);
    setNewIncident(null);
    setEditedIncident(null);
    setFormErrors({});
    setAmbiguousCandidates(null);
  };

  const convertDateISOToUTC = (isoDate) => {
    if (isoDate !== null && isoDate !== "") {
      const date = new Date(isoDate);
      const utcFormatted = `${date.getUTCFullYear()}-${String(
        date.getUTCMonth() + 1,
      ).padStart(2, "0")}-${String(date.getUTCDate()).padStart(
        2,
        "0",
      )} ${String(date.getUTCHours()).padStart(2, "0")}:${String(
        date.getUTCMinutes(),
      ).padStart(2, "0")}:${String(date.getUTCSeconds()).padStart(2, "0")}`;
      return utcFormatted;
    }
  };

  if (!editedIncident || !dropdowns) {
    return null;
  }

  const isCreateMode =
    editedIncident.id === null || editedIncident.id === undefined;

  // Rendered in BOTH create and edit mode. This used to live inside the
  // isCreateMode branch only, so an ambiguous resolve while editing stored the
  // candidates in state and then showed the operator nothing (#50).
  const ambiguityPicker = ambiguousCandidates ? (
    <Box
      mb={2}
      p={1.5}
      sx={{ border: "1px solid", borderColor: "warning.main", borderRadius: 1 }}
    >
      <Typography variant="subtitle2" gutterBottom>
        The location is ambiguous — select the intended location:
      </Typography>
      <Box display="flex" flexDirection="column" gap={1}>
        {ambiguousCandidates.map((candidate, index) => (
          <Button
            key={index}
            variant="outlined"
            size="small"
            sx={{ justifyContent: "flex-start", textTransform: "none" }}
            onClick={() => handlePickCandidate(candidate)}
          >
            {cleanCandidateDescription(candidate) || candidate.description}
          </Button>
        ))}
      </Box>
    </Box>
  ) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={true}
      disableEscapeKeyDown
    >
      <IconButton
        aria-label="close"
        sx={{
          position: "absolute",
          right: 10,
          top: 8,
          zIndex: 1,
        }}
        onClick={onClose}
      >
        <CloseIcon />
      </IconButton>
      <Card sx={{ borderRadius: 3, overflow: "auto" }}>
        <CardHeader
          title={
            <font className="layer-title">
              {editedIncident.id
                ? `Edit Incident ${editedIncident.id}`
                : "Create New Incident"}
            </font>
          }
          subheader={
            editedIncident.id ? (
              <>
                <div>
                  {" "}
                  Source: {editedIncident.sourceName} (
                  {editedIncident.externalId})
                </div>
                <div>
                  {" "}
                  Last updated:{" "}
                  {convertDateISOToUTC(editedIncident.lastUpdateTime)}
                </div>
              </>
            ) : null
          }
          align="center"
          className="styledtablerow"
        />
        <CardContent
          sx={{
            paddingTop: 1,
            paddingLeft: 2,
            paddingRight: 2,
            overflow: "auto",
          }}
        >
          <Paper elevation={2} sx={{ p: 1, mb: 1 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "primary.main", fontWeight: 600, mb: 2 }}
            >
              Location Information
            </Typography>

            {isCreateMode ? (
              <>
                <Box mb={2}>
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} md={8}>
                      <TextField
                        required
                        fullWidth
                        size="small"
                        label="Location Description"
                        placeholder="Enter location"
                        value={editedIncident.locationDescription || ""}
                        onChange={handleChange("locationDescription")}
                        error={!!formErrors.locationDescription}
                        helperText={
                          formErrors.locationDescription ||
                          "Location must be resolved or User Lane Count must be specified"
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        fullWidth
                        onClick={handleResolveLocation}
                        sx={{ height: 40 }}
                      >
                        Resolve Location
                      </Button>
                    </Grid>
                  </Grid>
                </Box>

                {ambiguityPicker}

                {editedIncident.resolvedLocation && (
                  <Box mb={2}>
                    <Typography variant="body2" sx={{ color: "success.main" }}>
                      ✓ Location resolved
                      {editedIncident.locations?.[0]
                        ? ": " +
                          (cleanCandidateDescription(
                            editedIncident.locations[0],
                          ) || editedIncident.locations[0].description)
                        : ""}
                    </Typography>
                  </Box>
                )}

                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: 500, mt: 1, mb: 1 }}
                >
                  Lanes
                </Typography>

                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={6} md={2}>
                    <TextField
                      size="small"
                      type="number"
                      label="Lane Count"
                      value={editedIncident.maxLaneCount || ""}
                      disabled
                      fullWidth
                      helperText="Auto-filled by Resolve Location"
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Box sx={{ pt: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={editedIncident.createUnresolved || false}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setEditedIncident((prev) => ({
                                ...prev,
                                createUnresolved: checked,
                                userLaneCount: checked ? prev.userLaneCount : 0,
                              }));
                            }}
                            disabled={!!editedIncident.resolvedLocation}
                          />
                        }
                        label="Create with unresolved location"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      size="small"
                      type="number"
                      label="User Lane Count"
                      value={editedIncident.userLaneCount || ""}
                      onChange={handleLaneCountChange("userLaneCount")}
                      disabled={
                        !editedIncident.createUnresolved ||
                        editedIncident.fullClosure ||
                        editedIncident.variousLanes
                      }
                      fullWidth
                      helperText="Manual lane count for unresolved locations"
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Lane Type</InputLabel>
                      <Select
                        value={editedIncident.laneType || ""}
                        label="Lane Type"
                        onChange={handleChange("laneType")}
                      >
                        {dropdowns.laneTypes?.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={2} md={1}>
                    <Box sx={{ pt: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={editedIncident.fullClosure || false}
                            onChange={handleCheckboxChange("fullClosure")}
                            disabled={editedIncident.variousLanes || false}
                          />
                        }
                        label="Full Closure"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={4} md={2}>
                    <Box sx={{ pt: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={editedIncident.variousLanes || false}
                            onChange={handleCheckboxChange("variousLanes")}
                            disabled={editedIncident.fullClosure || false}
                          />
                        }
                        label="Various Lanes"
                      />
                    </Box>
                  </Grid>
                </Grid>

                {
                  <Box mt={1} p={1} sx={{ borderRadius: 1 }}>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 500, mb: 2 }}
                    >
                      Lane Impacts
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={4} md={2}>
                        <Box display="flex" flexDirection="column" gap={1}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "medium" }}
                          >
                            Left Shoulder
                          </Typography>
                          <FormControl
                            fullWidth
                            size="small"
                            disabled={
                              editedIncident.fullClosure ||
                              editedIncident.variousLanes ||
                              !isNoShoulderImpact(
                                editedIncident.unspecifiedShoulder,
                                dropdowns.shoulderImpacts,
                              )
                            }
                          >
                            <InputLabel>Impact</InputLabel>
                            <Select
                              value={editedIncident.leftShoulder || ""}
                              label="Impact"
                              onChange={(e) => {
                                setEditedIncident((prev) => ({
                                  ...prev,
                                  leftShoulder: e.target.value,
                                  ...(e.target.value
                                    ? { unspecifiedShoulder: "" }
                                    : {}),
                                }));
                              }}
                            >
                              {dropdowns.shoulderImpacts?.map((impact) => (
                                <MenuItem
                                  key={impact.value}
                                  value={impact.value}
                                >
                                  {impact.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      </Grid>

                      {Array.from(
                        {
                          length:
                            editedIncident.userLaneCount ||
                            editedIncident.maxLaneCount ||
                            0,
                        },
                        (_, index) => {
                          const laneNumber = index + 1;
                          const currentImpact =
                            editedIncident.laneImpacts?.find(
                              (li) => li.laneNumber === laneNumber,
                            )?.impact || "";

                          return (
                            <Grid item xs={6} sm={4} md={2} key={laneNumber}>
                              <Box
                                display="flex"
                                flexDirection="column"
                                gap={1}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: "medium" }}
                                >
                                  Lane {laneNumber}
                                </Typography>
                                <FormControl
                                  fullWidth
                                  size="small"
                                  disabled={
                                    editedIncident.fullClosure ||
                                    editedIncident.variousLanes
                                  }
                                >
                                  <InputLabel>Impact</InputLabel>
                                  <Select
                                    value={currentImpact}
                                    label="Impact"
                                    onChange={(e) => {
                                      const newLaneImpacts = [
                                        ...(editedIncident.laneImpacts || []),
                                      ];
                                      const existingIndex =
                                        newLaneImpacts.findIndex(
                                          (li) => li.laneNumber === laneNumber,
                                        );

                                      if (existingIndex >= 0) {
                                        if (e.target.value === "") {
                                          newLaneImpacts.splice(
                                            existingIndex,
                                            1,
                                          );
                                        } else {
                                          newLaneImpacts[existingIndex] = {
                                            ...newLaneImpacts[existingIndex],
                                            impact: e.target.value,
                                          };
                                        }
                                      } else if (e.target.value !== "") {
                                        newLaneImpacts.push({
                                          laneNumber: laneNumber,
                                          impact: e.target.value,
                                        });
                                      }

                                      setEditedIncident((prev) => ({
                                        ...prev,
                                        laneImpacts: newLaneImpacts,
                                      }));
                                    }}
                                  >
                                    {dropdowns.laneImpacts?.map((impact) => (
                                      <MenuItem
                                        key={impact.value}
                                        value={impact.value}
                                      >
                                        {impact.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Box>
                            </Grid>
                          );
                        },
                      )}

                      <Grid item xs={6} sm={4} md={2}>
                        <Box display="flex" flexDirection="column" gap={1}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "medium" }}
                          >
                            Right Shoulder
                          </Typography>
                          <FormControl
                            fullWidth
                            size="small"
                            disabled={
                              editedIncident.fullClosure ||
                              editedIncident.variousLanes ||
                              !isNoShoulderImpact(
                                editedIncident.unspecifiedShoulder,
                                dropdowns.shoulderImpacts,
                              )
                            }
                          >
                            <InputLabel>Impact</InputLabel>
                            <Select
                              value={editedIncident.rightShoulder || ""}
                              label="Impact"
                              onChange={(e) => {
                                setEditedIncident((prev) => ({
                                  ...prev,
                                  rightShoulder: e.target.value,
                                  ...(e.target.value
                                    ? { unspecifiedShoulder: "" }
                                    : {}),
                                }));
                              }}
                            >
                              {dropdowns.shoulderImpacts?.map((impact) => (
                                <MenuItem
                                  key={impact.value}
                                  value={impact.value}
                                >
                                  {impact.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      </Grid>

                      <Grid item xs={6} sm={4} md={2}>
                        <Box display="flex" flexDirection="column" gap={1}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "medium" }}
                          >
                            Unspecified Shoulder
                          </Typography>
                          <FormControl
                            fullWidth
                            size="small"
                            disabled={
                              editedIncident.fullClosure ||
                              editedIncident.variousLanes
                            }
                          >
                            <InputLabel>Impact</InputLabel>
                            <Select
                              value={editedIncident.unspecifiedShoulder || ""}
                              label="Impact"
                              onChange={(e) => {
                                setEditedIncident((prev) => ({
                                  ...prev,
                                  unspecifiedShoulder: e.target.value,
                                  ...(e.target.value
                                    ? { leftShoulder: "", rightShoulder: "" }
                                    : {}),
                                }));
                              }}
                            >
                              {dropdowns.shoulderImpacts?.map((impact) => (
                                <MenuItem
                                  key={impact.value}
                                  value={impact.value}
                                >
                                  {impact.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                }
              </>
            ) : (
              <>
                {editedIncident.locations &&
                editedIncident.locations.length > 0 ? (
                  editedIncident.locations.map((location, locationIndex) => (
                    <Box
                      key={locationIndex}
                      mb={2}
                      p={2}
                      sx={{ borderRadius: 1 }}
                    >
                      <Grid container spacing={2} alignItems="flex-start">
                        <Grid item xs={12} md={8}>
                          <TextField
                            fullWidth
                            multiline
                            size="small"
                            label="Location Description"
                            value={location.description || ""}
                            onChange={(e) => {
                              const newLocations = [
                                ...editedIncident.locations,
                              ];
                              newLocations[locationIndex] = {
                                ...newLocations[locationIndex],
                                description: e.target.value,
                              };
                              setEditedIncident((prev) => ({
                                ...prev,
                                locations: newLocations,
                              }));
                            }}
                          />
                        </Grid>
                        {locationIndex === 0 && (
                          <Grid item xs={12} md={4}>
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              fullWidth
                              onClick={handleResolveLocation}
                              sx={{ height: 40 }}
                            >
                              Resolve Location
                            </Button>
                          </Grid>
                        )}
                      </Grid>

                      {locationIndex === 0 && ambiguityPicker}

                      <Box mt={1}>
                        <Typography variant="subtitle1" gutterBottom>
                          Lanes
                        </Typography>
                        <Grid container spacing={2} pb={2}>
                          <Grid item xs={4} md={1}>
                            <TextField
                              size="small"
                              type="number"
                              label="Lane Count"
                              value={location.laneCount || ""}
                              onChange={(e) => {
                                const newCount = parseInt(e.target.value) || 0;
                                const newLaneImpacts =
                                  newCount > 0
                                    ? Array.from(
                                        { length: newCount },
                                        (_, i) => ({
                                          laneNumber: i + 1,
                                          impact: "Open",
                                        }),
                                      )
                                    : [];
                                const newLocations = [
                                  ...editedIncident.locations,
                                ];
                                newLocations[locationIndex] = {
                                  ...newLocations[locationIndex],
                                  laneCount: newCount,
                                  lanes: newLaneImpacts,
                                };
                                setEditedIncident((prev) => ({
                                  ...prev,
                                  laneImpacts: newLaneImpacts,
                                  locations: newLocations,
                                }));
                              }}
                            />
                          </Grid>
                          <Grid item xs={6} md={2}>
                            <Box sx={{ pt: 0.5 }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={
                                      editedIncident.createUnresolved || false
                                    }
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      setEditedIncident((prev) => ({
                                        ...prev,
                                        createUnresolved: checked,
                                        userLaneCount: checked
                                          ? prev.userLaneCount
                                          : 0,
                                      }));
                                    }}
                                    disabled={!!editedIncident.resolvedLocation}
                                  />
                                }
                                label="Update with unresolved location"
                              />
                            </Box>
                          </Grid>
                          <Grid item xs={4} md={2}>
                            <TextField
                              size="small"
                              type="number"
                              label="User Lane Count"
                              value={editedIncident.userLaneCount || ""}
                              onChange={handleLaneCountChange("userLaneCount")}
                              helperText="Manual lane count for unresolved locations"
                            />
                          </Grid>
                          <Grid item xs={4} md={2}>
                            <FormControl fullWidth size="small">
                              <InputLabel> Lane Type </InputLabel>
                              <Select
                                value={editedIncident.laneType || ""}
                                label="Lane Type"
                                onChange={handleChange("laneType")}
                              >
                                {dropdowns.laneTypes?.map((type) => (
                                  <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={4} md={1}>
                            <TextField
                              size="small"
                              type="number"
                              label="Max Lane Count"
                              value={editedIncident.maxLaneCount || ""}
                              onChange={handleChange("maxLaneCount")}
                            />
                          </Grid>

                          <Grid item xs={6} md={2}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={
                                    location.fullClosure ??
                                    editedIncident.fullClosure ??
                                    false
                                  }
                                  onChange={(e) => {
                                    const newLocations = [
                                      ...editedIncident.locations,
                                    ];
                                    newLocations[locationIndex] = {
                                      ...newLocations[locationIndex],
                                      fullClosure: e.target.checked,
                                    };
                                    setEditedIncident((prev) => ({
                                      ...prev,
                                      fullClosure: e.target.checked,
                                      locations: newLocations,
                                    }));
                                  }}
                                  disabled={
                                    location.variousLanes ??
                                    editedIncident.variousLanes ??
                                    false
                                  }
                                />
                              }
                              label="Full Closure"
                            />
                          </Grid>
                          <Grid item xs={6} md={2}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={
                                    location.variousLanes ??
                                    editedIncident.variousLanes ??
                                    false
                                  }
                                  onChange={(e) => {
                                    const newLocations = [
                                      ...editedIncident.locations,
                                    ];
                                    newLocations[locationIndex] = {
                                      ...newLocations[locationIndex],
                                      variousLanes: e.target.checked,
                                    };
                                    setEditedIncident((prev) => ({
                                      ...prev,
                                      variousLanes: e.target.checked,
                                      locations: newLocations,
                                    }));
                                  }}
                                  disabled={
                                    location.fullClosure ??
                                    editedIncident.fullClosure ??
                                    false
                                  }
                                />
                              }
                              label="Various Lanes"
                            />
                          </Grid>
                        </Grid>
                        {editedIncident.userLaneCount > 0 ||
                        location.laneCount > 0 ||
                        (location.lanes && location.lanes.length > 0) ||
                        location.leftShoulder ||
                        location.rightShoulder ||
                        location.unspecifiedShoulder ||
                        editedIncident.leftShoulder ||
                        editedIncident.rightShoulder ||
                        editedIncident.unspecifiedShoulder ? (
                          <Box mt={1}>
                            <Typography variant="h6" gutterBottom>
                              Lane Impacts
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid
                                item
                                xs={6}
                                sm={4}
                                md={2}
                                key="leftShoulder"
                              >
                                <Box
                                  display="flex"
                                  flexDirection="column"
                                  gap={1}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: "medium" }}
                                  >
                                    Left Shoulder
                                  </Typography>
                                  <FormControl
                                    fullWidth
                                    size="small"
                                    disabled={
                                      (location.fullClosure ??
                                        editedIncident.fullClosure ??
                                        false) ||
                                      (location.variousLanes ??
                                        editedIncident.variousLanes ??
                                        false) ||
                                      !isNoShoulderImpact(
                                        location.unspecifiedShoulder ||
                                          editedIncident.unspecifiedShoulder,
                                        dropdowns.shoulderImpacts,
                                      )
                                    }
                                  >
                                    <InputLabel>Impact</InputLabel>
                                    <Select
                                      value={
                                        location.leftShoulder ||
                                        editedIncident.leftShoulder ||
                                        ""
                                      }
                                      label="Impact"
                                      onChange={(e) => {
                                        const newLocations = [
                                          ...editedIncident.locations,
                                        ];
                                        newLocations[locationIndex] = {
                                          ...newLocations[locationIndex],
                                          leftShoulder: e.target.value,
                                          ...(e.target.value
                                            ? { unspecifiedShoulder: "" }
                                            : {}),
                                        };
                                        setEditedIncident((prev) => ({
                                          ...prev,
                                          leftShoulder: e.target.value,
                                          ...(e.target.value
                                            ? { unspecifiedShoulder: "" }
                                            : {}),
                                          locations: newLocations,
                                        }));
                                      }}
                                    >
                                      {dropdowns.shoulderImpacts?.map(
                                        (impact) => (
                                          <MenuItem
                                            key={impact.value}
                                            value={impact.value}
                                          >
                                            {impact.label}
                                          </MenuItem>
                                        ),
                                      )}
                                    </Select>
                                  </FormControl>
                                </Box>
                              </Grid>
                              {Array.from(
                                {
                                  length:
                                    editedIncident.userLaneCount ||
                                    location.laneCount ||
                                    0,
                                },
                                (_, index) => {
                                  const laneNumber = index + 1;
                                  const currentImpact =
                                    location.lanes?.find(
                                      (li) => li.laneNumber === laneNumber,
                                    )?.impact ||
                                    editedIncident.laneImpacts?.find(
                                      (li) => li.laneNumber === laneNumber,
                                    )?.impact ||
                                    "";

                                  return (
                                    <Grid
                                      item
                                      xs={6}
                                      sm={4}
                                      md={2}
                                      key={laneNumber}
                                    >
                                      <Box
                                        display="flex"
                                        flexDirection="column"
                                        gap={1}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            minWidth: "60px",
                                            fontWeight: "medium",
                                          }}
                                        >
                                          Lane {laneNumber}:
                                        </Typography>
                                        <FormControl
                                          fullWidth
                                          size="small"
                                          disabled={
                                            (location.fullClosure ??
                                              editedIncident.fullClosure ??
                                              false) ||
                                            (location.variousLanes ??
                                              editedIncident.variousLanes ??
                                              false)
                                          }
                                        >
                                          <InputLabel>Impact</InputLabel>
                                          <Select
                                            value={currentImpact}
                                            label="Impact"
                                            onChange={(e) => {
                                              const newLaneImpacts = [
                                                ...(editedIncident.laneImpacts ||
                                                  []),
                                              ];
                                              const existingImpactIndex =
                                                newLaneImpacts.findIndex(
                                                  (li) =>
                                                    li.laneNumber ===
                                                    laneNumber,
                                                );
                                              if (existingImpactIndex >= 0) {
                                                if (e.target.value === "") {
                                                  newLaneImpacts.splice(
                                                    existingImpactIndex,
                                                    1,
                                                  );
                                                } else {
                                                  newLaneImpacts[
                                                    existingImpactIndex
                                                  ] = {
                                                    ...newLaneImpacts[
                                                      existingImpactIndex
                                                    ],
                                                    impact: e.target.value,
                                                  };
                                                }
                                              } else if (
                                                e.target.value !== ""
                                              ) {
                                                newLaneImpacts.push({
                                                  laneNumber: laneNumber,
                                                  impact: e.target.value,
                                                });
                                              }

                                              // Keep location.lanes in sync with laneImpacts
                                              const newLocations = [
                                                ...editedIncident.locations,
                                              ];
                                              newLocations[locationIndex] = {
                                                ...newLocations[locationIndex],
                                                lanes: newLaneImpacts,
                                              };

                                              setEditedIncident((prev) => ({
                                                ...prev,
                                                laneImpacts: newLaneImpacts,
                                                locations: newLocations,
                                              }));
                                            }}
                                          >
                                            {dropdowns.laneImpacts?.map(
                                              (impact) => (
                                                <MenuItem
                                                  key={impact.value}
                                                  value={impact.value}
                                                >
                                                  {impact.label}
                                                </MenuItem>
                                              ),
                                            )}
                                          </Select>
                                        </FormControl>
                                      </Box>
                                    </Grid>
                                  );
                                },
                              )}
                              <Grid item xs={6} sm={4} md={2}>
                                <Box
                                  display="flex"
                                  flexDirection="column"
                                  gap={1}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: "medium" }}
                                  >
                                    Right Shoulder
                                  </Typography>
                                  <FormControl
                                    fullWidth
                                    size="small"
                                    disabled={
                                      (location.fullClosure ??
                                        editedIncident.fullClosure ??
                                        false) ||
                                      (location.variousLanes ??
                                        editedIncident.variousLanes ??
                                        false) ||
                                      !isNoShoulderImpact(
                                        location.unspecifiedShoulder ||
                                          editedIncident.unspecifiedShoulder,
                                        dropdowns.shoulderImpacts,
                                      )
                                    }
                                  >
                                    <InputLabel>Impact</InputLabel>
                                    <Select
                                      value={
                                        location.rightShoulder ||
                                        editedIncident.rightShoulder ||
                                        ""
                                      }
                                      label="Right Shoulder"
                                      onChange={(e) => {
                                        const newLocations = [
                                          ...editedIncident.locations,
                                        ];
                                        newLocations[locationIndex] = {
                                          ...newLocations[locationIndex],
                                          rightShoulder: e.target.value,
                                          ...(e.target.value
                                            ? { unspecifiedShoulder: "" }
                                            : {}),
                                        };
                                        setEditedIncident((prev) => ({
                                          ...prev,
                                          rightShoulder: e.target.value,
                                          ...(e.target.value
                                            ? { unspecifiedShoulder: "" }
                                            : {}),
                                          locations: newLocations,
                                        }));
                                      }}
                                    >
                                      {dropdowns.shoulderImpacts?.map(
                                        (impact) => (
                                          <MenuItem
                                            key={impact.value}
                                            value={impact.value}
                                          >
                                            {impact.label}
                                          </MenuItem>
                                        ),
                                      )}
                                    </Select>
                                  </FormControl>
                                </Box>
                              </Grid>

                              <Grid
                                item
                                xs={6}
                                sm={4}
                                md={2}
                                key="unspecifiedShoulder"
                              >
                                <Box
                                  display="flex"
                                  flexDirection="column"
                                  gap={1}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: "medium" }}
                                  >
                                    Unspecified Shoulder
                                  </Typography>
                                  <FormControl
                                    fullWidth
                                    size="small"
                                    disabled={
                                      (location.fullClosure ??
                                        editedIncident.fullClosure ??
                                        false) ||
                                      (location.variousLanes ??
                                        editedIncident.variousLanes ??
                                        false)
                                    }
                                  >
                                    <InputLabel>Impact</InputLabel>
                                    <Select
                                      value={
                                        location.unspecifiedShoulder ||
                                        editedIncident.unspecifiedShoulder ||
                                        ""
                                      }
                                      label="Unspecified Shoulder"
                                      onChange={(e) => {
                                        const newLocations = [
                                          ...editedIncident.locations,
                                        ];
                                        newLocations[locationIndex] = {
                                          ...newLocations[locationIndex],
                                          unspecifiedShoulder: e.target.value,
                                          ...(e.target.value
                                            ? {
                                                leftShoulder: "",
                                                rightShoulder: "",
                                              }
                                            : {}),
                                        };
                                        setEditedIncident((prev) => ({
                                          ...prev,
                                          unspecifiedShoulder: e.target.value,
                                          ...(e.target.value
                                            ? {
                                                leftShoulder: "",
                                                rightShoulder: "",
                                              }
                                            : {}),
                                          locations: newLocations,
                                        }));
                                      }}
                                    >
                                      {dropdowns.shoulderImpacts?.map(
                                        (impact) => (
                                          <MenuItem
                                            key={impact.value}
                                            value={impact.value}
                                          >
                                            {impact.label}
                                          </MenuItem>
                                        ),
                                      )}
                                    </Select>
                                  </FormControl>
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            No lanes defined for this location
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No locations defined
                  </Typography>
                )}
              </>
            )}
          </Paper>
          <Paper elevation={2} sx={{ p: 1, mb: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                  label="Incident Description"
                  value={editedIncident.description || ""}
                  onChange={handleChange("description")}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                  label="Comments"
                  value={editedIncident.comments || ""}
                  onChange={handleChange("comments")}
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={2} sx={{ p: 1, mb: 1 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "primary.main", fontWeight: 600, mb: 2 }}
            >
              Incident Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} md={2}>
                <FormControl
                  fullWidth
                  size="small"
                  error={!!formErrors.sourceName}
                >
                  <InputLabel>Source Name </InputLabel>
                  <Select
                    required
                    value={editedIncident.sourceName || ""}
                    label="Source Name "
                    onChange={handleChange("sourceName")}
                  >
                    {dropdowns.sources?.map((source) => (
                      <MenuItem key={source.id} value={source.name}>
                        {source.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.sourceName && (
                    <FormHelperText>{formErrors.sourceName}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="State"
                  value={editedIncident.state || ""}
                  onChange={handleChange("state")}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <FormControl
                  fullWidth
                  size="small"
                  error={!!formErrors.confidenceLevel}
                >
                  <InputLabel>Confidence Level </InputLabel>
                  <Select
                    required
                    value={editedIncident.confidenceLevel || ""}
                    label="Confidence Level "
                    onChange={handleChange("confidenceLevel")}
                  >
                    {dropdowns.confidenceLevels?.map((level) => (
                      <MenuItem key={level.value} value={level.value}>
                        {level.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.confidenceLevel && (
                    <FormHelperText>
                      {formErrors.confidenceLevel}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Severity"
                  value={editedIncident.severity || ""}
                  onChange={handleChange("severity")}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Roadway Condition</InputLabel>
                  <Select
                    value={editedIncident.roadwayCondition || ""}
                    label="Roadway Condition"
                    onChange={handleChange("roadwayCondition")}
                  >
                    {dropdowns.roadwayConditions?.map((condition) => (
                      <MenuItem key={condition.value} value={condition.value}>
                        {condition.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Weather Condition</InputLabel>
                  <Select
                    value={editedIncident.weather || ""}
                    label="Weather"
                    onChange={handleChange("weather")}
                  >
                    {dropdowns.weatherConditions?.map((weather) => (
                      <MenuItem key={weather.value} value={weather.value}>
                        {weather.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Detection Type</InputLabel>
                  <Select
                    value={editedIncident.detectionType || ""}
                    label="Detection Type"
                    onChange={handleChange("detectionType")}
                  >
                    {dropdowns.detectionTypes?.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Verification Type</InputLabel>
                  <Select
                    value={editedIncident.verificationType || ""}
                    label="Verification Type"
                    onChange={handleChange("verificationType")}
                  >
                    {dropdowns.verificationTypes?.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={2} sx={{ p: 1, mb: 1 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "primary.main", fontWeight: 600, mb: 2 }}
            >
              Vehicle Counts
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={2} sm={1}>
                <FormControl fullWidth size="small">
                  <InputLabel>Automobile Count</InputLabel>
                  <Select
                    value={editedIncident.automobileCount || 0}
                    label="Automobile Count"
                    onChange={handleChange("automobileCount")}
                  >
                    {dropdowns.counts?.map((count) => (
                      <MenuItem key={count} value={count}>
                        {count}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={2} sm={1}>
                <FormControl fullWidth size="small">
                  <InputLabel>Bus Count</InputLabel>
                  <Select
                    value={editedIncident.busCount || 0}
                    label="Bus Count"
                    onChange={handleChange("busCount")}
                  >
                    {dropdowns.counts?.map((count) => (
                      <MenuItem key={count} value={count}>
                        {count}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={2} sm={1}>
                <FormControl fullWidth size="small">
                  <InputLabel>Motorcycle Count</InputLabel>
                  <Select
                    value={editedIncident.motorcycleCount || 0}
                    label="Motorcycle Count"
                    onChange={handleChange("motorcycleCount")}
                  >
                    {dropdowns.counts?.map((count) => (
                      <MenuItem key={count} value={count}>
                        {count}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={2} sm={1}>
                <FormControl fullWidth size="small">
                  <InputLabel>Pickup Truck Count</InputLabel>
                  <Select
                    value={editedIncident.pickupTruckCount || 0}
                    label="Pickup Truck Count"
                    onChange={handleChange("pickupTruckCount")}
                  >
                    {dropdowns.counts?.map((count) => (
                      <MenuItem key={count} value={count}>
                        {count}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={2} sm={1}>
                <FormControl fullWidth size="small">
                  <InputLabel>Semi Trailer Count</InputLabel>
                  <Select
                    value={editedIncident.semiTrailerCount || 0}
                    label="Semi Trailer Count"
                    onChange={handleChange("semiTrailerCount")}
                  >
                    {dropdowns.counts?.map((count) => (
                      <MenuItem key={count} value={count}>
                        {count}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={2} sm={1}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tanker Truck Count</InputLabel>
                  <Select
                    value={editedIncident.tankerTruckCount || 0}
                    label="Tanker Truck Count"
                    onChange={handleChange("tankerTruckCount")}
                  >
                    {dropdowns.counts?.map((count) => (
                      <MenuItem key={count} value={count}>
                        {count}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={2} sm={1}>
                <FormControl fullWidth size="small">
                  <InputLabel>Fatality Count</InputLabel>
                  <Select
                    value={editedIncident.fatalityCount || 0}
                    label="Fatality Count"
                    onChange={handleChange("fatalityCount")}
                  >
                    {dropdowns.counts?.map((count) => (
                      <MenuItem key={count} value={count}>
                        {count}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={2} sm={1}>
                <FormControl fullWidth size="small">
                  <InputLabel>Injury Count</InputLabel>
                  <Select
                    value={editedIncident.injuryCount || 0}
                    label="Injury Count"
                    onChange={handleChange("injuryCount")}
                  >
                    {dropdowns.counts?.map((count) => (
                      <MenuItem key={count} value={count}>
                        {count}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={2} sx={{ p: 1, mb: 1 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "primary.main", fontWeight: 600, mb: 2 }}
            >
              Incident Features
            </Typography>
            <FormGroup row>
              <Grid container spacing={2}>
                <Grid item xs={6} md={2.4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editedIncident.accident || false}
                        onChange={handleCheckboxChange("accident")}
                      />
                    }
                    label="Accident"
                  />
                </Grid>
                <Grid item xs={6} md={2.4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editedIncident.fire || false}
                        onChange={handleCheckboxChange("fire")}
                      />
                    }
                    label="Fire"
                  />
                </Grid>
                <Grid item xs={6} md={2.4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editedIncident.hazmat || false}
                        onChange={handleCheckboxChange("hazmat")}
                      />
                    }
                    label="Hazmat"
                  />
                </Grid>
                <Grid item xs={6} md={2.4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editedIncident.policeAction || false}
                        onChange={handleCheckboxChange("policeAction")}
                      />
                    }
                    label="Police Action"
                  />
                </Grid>
                <Grid item xs={6} md={2.4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editedIncident.medicalEmergency || false}
                        onChange={handleCheckboxChange("medicalEmergency")}
                      />
                    }
                    label="Medical Emergency"
                  />
                </Grid>
              </Grid>
            </FormGroup>
          </Paper>

          <Paper elevation={2} sx={{ p: 1, mb: 1 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "primary.main", fontWeight: 600, mb: 2 }}
            >
              Time Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <DateTimePicker
                  label="Occurrence Time"
                  value={editedIncident.occurrenceTime}
                  onChange={handleDateTimeChange("occurrenceTime")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      error={!!formErrors.occurrenceTime}
                      helperText={formErrors.occurrenceTime}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DateTimePicker
                  label="Detection Time"
                  value={editedIncident.detectionTime}
                  onChange={handleDateTimeChange("detectionTime")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      error={!!formErrors.detectionTime}
                      helperText={formErrors.detectionTime}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DateTimePicker
                  label="Verification Time"
                  value={editedIncident.verificationTime}
                  onChange={handleDateTimeChange("verificationTime")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      error={!!formErrors.verificationTime}
                      helperText={formErrors.verificationTime}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DateTimePicker
                  label="Estimated Closure Time"
                  value={editedIncident.estimatedClosureTime}
                  onChange={handleDateTimeChange("estimatedClosureTime")}
                  renderInput={(params) => (
                    <TextField {...params} size="small" fullWidth />
                  )}
                />
              </Grid>
            </Grid>
          </Paper>

          <Box display="flex" justifyContent="center" gap={2} pb={2}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleSave}
              sx={{ minWidth: 150 }}
            >
              Save Incident
            </Button>
            {editedIncident.id && (
              <Button
                variant="contained"
                size="small"
                onClick={handleClose}
                sx={{ minWidth: 150 }}
              >
                Close Incident
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              onClick={onClose}
              sx={{ minWidth: 150 }}
            >
              Cancel
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Dialog>
  );
};

export default EditIncidentForm;
